const API_BASE = '/api/v1/supplier';

async function parseJson(res: Response) {
  const text = await res.text();
  let data: any = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(res.ok ? 'Unexpected response from server' : `Request failed (HTTP ${res.status})`);
    }
  }
  if (!res.ok || data.success === false) {
    if (!data.error && !data.message) {
      throw new Error(`Request failed (HTTP ${res.status})`);
    }
    throw new Error(data.error || data.message || 'Request failed');
  }
  return data;
}

export interface SupplierPoSummary {
  id: number;
  restroId: number;
  poNumber: string;
  status: string;
  supplierName?: string;
  restaurantName?: string;
  orderDate?: string;
  expectedDate?: string;
  grandTotal?: number;
}

export interface PoProposalLine {
  poItemId: number;
  proposedQty?: number;
  proposedPrice?: number;
}

export interface SupplierPoProposePayload {
  lines: PoProposalLine[];
  deliveryDate?: string;
}

export async function getSupplierDashboard() {
  const res = await fetch(`${API_BASE}/dashboard`, { credentials: 'include' });
  return parseJson(res);
}

export async function getLinkedRestaurants() {
  const res = await fetch(`${API_BASE}/restaurants`, { credentials: 'include' });
  return parseJson(res);
}

export async function listSupplierOrders(status?: string) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetch(`${API_BASE}/po${q}`, { credentials: 'include' });
  return parseJson(res);
}

export async function getSupplierOrder(restroId: number, poId: number) {
  const res = await fetch(`${API_BASE}/po/${restroId}/${poId}`, { credentials: 'include' });
  return parseJson(res);
}

export async function proposeSupplierOrder(restroId: number, poId: number, payload: SupplierPoProposePayload) {
  const res = await fetch(`${API_BASE}/po/${restroId}/${poId}/propose`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

export async function dispatchSupplierOrder(restroId: number, poId: number) {
  const res = await fetch(`${API_BASE}/po/${restroId}/${poId}/dispatch`, {
    method: 'POST',
    credentials: 'include',
  });
  return parseJson(res);
}

export async function getPoByRespondToken(token: string) {
  const res = await fetch(`${API_BASE}/po/respond/${token}`);
  return parseJson(res);
}

export async function proposeByRespondToken(token: string, payload: SupplierPoProposePayload) {
  const res = await fetch(`${API_BASE}/po/respond/${token}/propose`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

export interface SupplierOrganization {
  id: number;
  legalName?: string;
  tradeName?: string;
  gstin?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  /** JSON array text, e.g. ["Vegetables","Dairy"]. */
  categories?: string;
  isMarketplaceVisible?: boolean;
}

export type SupplierProfileUpdate = Omit<SupplierOrganization, 'id'>;

export async function getSupplierProfile(orgId: number) {
  const res = await fetch(`${API_BASE}/profile/${orgId}`, { credentials: 'include' });
  return parseJson(res);
}

export async function registerSupplierProfile(profile: SupplierProfileUpdate) {
  const res = await fetch(`${API_BASE}/profile`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(profile),
  });
  return parseJson(res);
}

export async function saveSupplierProfile(orgId: number, profile: SupplierProfileUpdate) {
  const res = await fetch(`${API_BASE}/profile/${orgId}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(profile),
  });
  return parseJson(res);
}

export async function updateCatalogStock(
  orgId: number,
  itemId: number,
  stockQuantity: number | null,
  isAvailable: boolean,
) {
  const res = await fetch(`${API_BASE}/catalog/${orgId}/stock/${itemId}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stockQuantity, isAvailable }),
  });
  return parseJson(res);
}

export interface SupplierCatalogItem {
  id?: number;
  orgId?: number;
  itemName: string;
  supplierSku?: string;
  category?: string;
  packSize?: number;
  listPrice?: number;
  hsn?: string;
  moq?: number;
  leadTimeDays?: number;
  isAvailable?: boolean;
  stockQuantity?: number | null;
}

export async function getOrganizations() {
  const res = await fetch(`${API_BASE}/orgs`, { credentials: 'include' });
  return parseJson(res);
}

export async function deleteCatalogItem(orgId: number, itemId: number) {
  const res = await fetch(`${API_BASE}/catalog/${orgId}/delete/${itemId}`, {
    method: 'POST',
    credentials: 'include',
  });
  return parseJson(res);
}

export async function importCatalog(orgId: number, csv: string) {
  const res = await fetch(`${API_BASE}/catalog/${orgId}/import`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ csv }),
  });
  return parseJson(res);
}

export async function getCatalog(orgId: number) {
  const res = await fetch(`${API_BASE}/catalog/${orgId}`, { credentials: 'include' });
  return parseJson(res);
}

export async function saveCatalogItem(orgId: number, item: SupplierCatalogItem) {
  const res = await fetch(`${API_BASE}/catalog/${orgId}/save`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(item),
  });
  return parseJson(res);
}

/** PO dates are date-only values sent as UTC-midnight timestamps; read them in UTC so the day never shifts. */
export function poDateToInput(value?: string | number | null): string {
  if (value == null || value === '') return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

export function formatPoDate(value?: string | number | null): string {
  if (value == null || value === '') return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-IN', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' });
}

export function todayInput(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export function formatPoStatus(status: string): string {
  const map: Record<string, string> = {
    PENDING_SUPPLIER: 'Awaiting your response',
    SUPPLIER_PROPOSED: 'Proposal sent',
    APPROVED: 'Approved — prepare dispatch',
    DISPATCHED: 'Dispatched',
    PARTIALLY_RECEIVED: 'Partially received',
    RECEIVED: 'Received',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
    DRAFT: 'Draft',
    PORTAL_ACTIVE: 'Connected',
    INVITE_SENT: 'Invite pending',
    NO_CONTACT: 'No portal access',
  };
  return map[status] || status;
}
